import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatBp, formatMoney } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { etiquetaListaPrecio } from '../lib/catalogo.js';
import { emitir } from '../lib/emitir.js';
import { preciosBajoMargen, type PrecioBajo } from '../lib/margenes.js';
import { useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/** The prices under the floor, from what is on this device; only these products, when given. */
export async function buscarPreciosBajos(soloProductos?: readonly string[]): Promise<PrecioBajo[]> {
  const [productos, unidades, precios, existencias, compras, config] = await Promise.all([
    db.productos.toArray(),
    db.unidades.toArray(),
    db.precios.toArray(),
    db.existencias.toArray(),
    db.compras.toArray(),
    readSettings(),
  ]);
  return preciosBajoMargen({
    productos,
    unidades,
    precios,
    existencias,
    compras,
    minimoBp: config.precioMargenMinimoBp,
    ...(soloProductos ? { soloProductos: new Set(soloProductos) } : {}),
  });
}

/** Kept up to date: how many prices sit under the floor right now. */
export function usePreciosBajoMargen(activo: boolean): number {
  return useLiveQuery(async () => (activo ? (await buscarPreciosBajos()).length : 0), [activo], 0);
}

/**
 * Prices a new cost left under the margin floor, each with a one-tap raise to the floor. Nothing
 * is raised on its own: a price kept low on purpose stays low, and the owner decides.
 */
export function PreciosBajoMargen({
  soloProductos,
  onCerrar,
}: {
  /** Only the products a purchase just brought in. */
  soloProductos?: readonly string[];
  onCerrar: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const bajos = useLiveQuery(() => buscarPreciosBajos(soloProductos), [soloProductos?.join(',')], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], null);
  const [subiendo, setSubiendo] = useState(false);

  async function subir(filas: readonly PrecioBajo[]) {
    setSubiendo(true);
    try {
      for (const f of filas) {
        await emitir('precio.fijado', { unidadVentaId: f.unidadVentaId, listaId: f.listaId, precio: f.sugerido });
      }
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <Modal
      titulo={t('margen.titulo')}
      onCerrar={onCerrar}
      pie={
        <div className="flex gap-3">
          <button type="button" className="btn-secundario flex-1" onClick={onCerrar}>
            <Ico icono="listo" />
            {t(bajos.length === 0 ? 'comun.listo' : 'margen.dejarlos')}
          </button>
          {bajos.length > 1 && (
            <button type="button" className="btn-primario flex-1" disabled={subiendo} onClick={() => void subir(bajos)}>
              <Ico icono="precios" />
              {t('margen.subirTodos', { n: bajos.length })}
            </button>
          )}
        </div>
      }
    >
      <p className="mb-4 text-sm text-slate-400">
        {bajos.length === 0
          ? t('margen.todosBien')
          : t('margen.explicacion', { minimo: formatBp(config?.precioMargenMinimoBp ?? 0) })}
      </p>
      <ul className="flex flex-col divide-y divide-slate-800">
        {bajos.map((f) => {
          const producto = productos.find((p) => p.id === f.productoId);
          const unidad = unidades.find((u) => u.id === f.unidadVentaId);
          const lista = listas.find((l) => l.id === f.listaId);
          return (
            <li key={`${f.unidadVentaId}:${f.listaId}`} className="flex items-center gap-3 py-3">
              <span className="min-w-0 flex-1">
                <span className="block break-words font-medium">{producto ? nombre(producto) : '—'}</span>
                <span className="block text-xs text-slate-500">
                  {unidad?.nombre}
                  {lista ? ` · ${etiquetaListaPrecio(lista)}` : ''}
                </span>
                <span className="tabular block text-sm">
                  <span className="text-rose-300">
                    {formatMoney(f.precio)} · {formatBp(f.margenBp)}
                  </span>
                  <span className="text-slate-400"> → {formatMoney(f.sugerido)}</span>
                </span>
              </span>
              <button
                type="button"
                className="btn-secundario shrink-0 px-4 text-sm"
                disabled={subiendo}
                onClick={() => void subir([f])}
              >
                <Ico icono="precios" className="size-4" />
                {t('margen.subir')}
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
